/* eslint-disable react/require-default-props */
import React from 'react';
import {PixelRatio, View, ViewStyle} from 'react-native';
import {AccessibilityRole} from 'react-native';
import {isEmpty, isFunction} from 'lodash';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {getTextFromReactNode} from '/util/accessibility.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ProfileAttribute.style';
interface ProfileAttributeProps {
  style: {
    base?: ViewStyle;
  };
  iconName?: string;
  isDisabled?: boolean;
  testID?: string;
  label?: React.ReactNode;
  value?: React.ReactNode;
  onPress?: () => void;
  onLongPress?: () => void;
  isDetails?: boolean;
  isArrow?: boolean;
  showIcon?: boolean;
  valueTextProps?: Record<string, unknown>;
  iscandidateDisposition?: boolean;
  accessibilityRole: string;
  accessibilityLabel?: string;
}

const ProfileAttribute: React.FC<ProfileAttributeProps> = ({
  style = {},
  iconName = 'id',
  isDisabled = false,
  label = '',
  value = '',
  onPress,
  onLongPress,
  testID,
  isDetails = false,
  isArrow = false,
  showIcon = true,
  valueTextProps = {},
  iscandidateDisposition = false,
  accessibilityRole,
  accessibilityLabel,
}) => {
  const isTouchable =
    !isDisabled && (isFunction(onPress) || isFunction(onLongPress));

  const combinedStyle = [styles.base, style.base];

  const role: AccessibilityRole = accessibilityRole
    ? (accessibilityRole as AccessibilityRole)
    : isTouchable
    ? 'button'
    : 'header';

  const BaseComponent = isTouchable ? TouchableOpacity : View;
  const baseComponentProps: any = {
    style: combinedStyle,
    ...(isTouchable
      ? {
          onPress,
          ...testProperties(null, true),
          onLongPress,
          accessible: true,
          accessibilityRole: role,
          accessibilityLabel:
            accessibilityLabel ||
            `${getTextFromReactNode(label)}: ${getTextFromReactNode(value)}`,
        }
      : {}),
  };

  const computedValueTextProps = {
    emphasis: 'medium',
    ...(isTouchable ? {useCompetency: true} : {}),
    ...valueTextProps,
  };
  const fontScale = PixelRatio.getFontScale();
  const safeEmail = String(value).replace(/-/g, '\u2011');

  return (
    <BaseComponent {...baseComponentProps}>
      {isDetails && showIcon && (
        <Icon
          name={iconName}
          style={styles.iconFlex}
          accessible={false}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
        />
      )}
      <View style={styles.dataAndRightIconContainer} accessible={false}>
        <View style={styles.dataContainer} accessible={false}>
          <View
            style={styles.firstRowContainer}
            accessible={true}
            accessibilityRole={role}
            accessibilityLabel={getTextFromReactNode(label)}>
            {!isDetails && showIcon && (
              <Icon
                name={iconName}
                style={styles.iconColumn}
                accessible={false}
              />
            )}
            <Text
              variant="heading"
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={testID ? `${testID}-label` : testID}
              style={styles.labelRow}
              accessible={false}>
              {label}
            </Text>
          </View>

          {!isEmpty(value) && (
            <View accessible={true} accessibilityLabel={value}>
              <Text
                accessible={true}
                style={
                  iscandidateDisposition
                    ? styles.valueRowDisposition
                    : isDisabled === true
                    ? styles.coachvalueRow
                    : styles.valueRow
                }
                {...computedValueTextProps}
                allowFontScaling
                maxFontSizeMultiplier={2}
                numberOfLines={fontScale > 1.3 ? undefined : 1}
                ellipsizeMode="clip"
                textBreakStrategy="simple"
                testID={testID ? `${testID}-value` : testID}
                accessibilityRole="text">
                {safeEmail}
              </Text>
            </View>
          )}
        </View>

        {isArrow && (
          <View style={styles.arrowIcon} accessible={false}>
            <Icon name="chevron-right" />
          </View>
        )}
      </View>
    </BaseComponent>
  );
};

export default ProfileAttribute;
